import { useCallback } from 'react';
import { useCollectionContext } from '../context/CollectionContext';
import PokemonTCGService from '../services/PokemonTCGService';
import * as CollectionAPI from '../services/CollectionAPI';
import { getCardVariants } from '../utils/cardVariants';
import { getParentSetId, mergeSetList, sortSetCards } from '../utils/setMerges';
import { MAX_DEX } from '../data/nationalDex';

/**
 * Custom hook for collection operations
 * Provides a clean interface for components to interact with collection data
 */
export const useCollection = () => {
  const context = useCollectionContext();
  
  const {
    collection,
    currentSet,
    cards,
    allSets,
    settings,
    filters,
    globalSearch,
    loading,
    serverOnline,
    currentView,
    selectedSeries,
    packStats,
    updateCardQuantity,
    getTotalQuantity,
    getVariantQuantity,
    updateSetPackCount,
    getSetPackCount,
    updateEnergyStat,
    getEnergyStats,
    getEnergyTypeStats,
    calculateCompletion,
    calculateSetCompletion,  // NEW: Added set completion function
    getCollectionStats,
    getSetTrackedHitRate,
    getGlobalHitRate,
    getPortfolioValue,
    portfolioLoading,
    getFilteredCards,
    getFilterOptions,
    setCurrentView,
    setLoading,
    setCards,
    setCurrentSet,
    setAllSets,
    setFilters,
    setGlobalSearch,
    setServerStatus,
    setSettings,
    pokedexBinder,
    setPokedexSlot,
    removePokedexSlotLocal,
    getPokedexSlot,
    getPokedexCompletion
  } = context;
  
  // Load all Pokemon sets
  const loadAllSets = useCallback(async () => {
    try {
      setLoading(true);
      const setsData = await PokemonTCGService.fetchSets();
      if (setsData?.data && Array.isArray(setsData.data)) {
        // Merged child sets (BUG-010) are hidden here and counted in their
        // parent's total — every set list downstream reads allSets.
        const sets = mergeSetList(setsData.data);
        setAllSets(sets);
        return sets;
      }
      return [];
    } catch (error) {
      console.error('Error loading sets:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setAllSets]);
  
  const loadSetCards = useCallback(async (set) => {
    try {
      setLoading(true);
      const cardsData = await PokemonTCGService.fetchMergedSetCards(set.id);

      // Merge-aware: a parent's own cards first, then each merged child's.
      const sortedData = sortSetCards(cardsData.data);

      const cardsWithVariants = sortedData.map(card => ({
        ...card,
        variants: getCardVariants(card)
      }));

      setCards(cardsWithVariants);
      setCurrentSet(set);
      
      // Clear filters when loading new set - UPDATED TO INCLUDE ARTIST
      setFilters({
        search: '',
        rarity: 'all',
        type: 'all',
        ownership: 'all',
        artist: 'all'
      });
      
      return cardsWithVariants;
    } catch (error) {
      console.error('Error loading cards:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setCards, setCurrentSet, setFilters]);
  
  // Load the 151 set specifically
  const load151Set = useCallback(async () => {
    try {
      setLoading(true);
      const set151 = await PokemonTCGService.findSet151();
      if (set151) {
        const cardsData = await PokemonTCGService.fetchSetCards(set151.id);
        const cardsWithVariants = cardsData.data.map(card => ({
          ...card,
          variants: getCardVariants(card)
        }));
        
        setCurrentSet(set151);
        setCards(cardsWithVariants);
        return { set: set151, cards: cardsWithVariants };
      }
      throw new Error('151 set not found');
    } catch (error) {
      console.error('Error loading 151 set:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setCurrentSet, setCards]);
  
  const performGlobalSearch = useCallback(async () => {
    return [];
  }, []);
  
  // Navigate to a specific card's set from global search
  const navigateToCardSet = useCallback(async (card) => {
    try {
      setLoading(true);
      
      // Open the set the card is DISPLAYED under: a merged child's card
      // (BUG-010 — a Classic Collection hit) opens its parent set. Prefer
      // the set-list entry over the search row's setInfo — it carries the
      // merged total.
      const setId = getParentSetId(card.setInfo.id);
      let set = allSets.find(s => s.id === setId);
      if (!set && setId !== card.setInfo.id) {
        const setsData = await PokemonTCGService.fetchSets();
        set = mergeSetList(setsData?.data || []).find(s => s.id === setId);
      }
      set = set || card.setInfo;

      // Load the set that contains this card
      const cardsData = await PokemonTCGService.fetchMergedSetCards(set.id);
      const cardsWithVariants = sortSetCards(cardsData.data).map(c => ({
        ...c,
        variants: getCardVariants(c)
      }));
      
      // Update context
      setCurrentSet(set);
      setCards(cardsWithVariants);
      setCurrentView('cards', {
        selectedSeries: set.series,
        currentSet: set
      });
      
      // Clear global search
      setGlobalSearch({ term: '', results: [], loading: false });
      
      return { set, cards: cardsWithVariants, targetCard: card };
    } catch (error) {
      console.error('Error navigating to card set:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [allSets, setLoading, setCurrentSet, setCards, setCurrentView, setGlobalSearch]);
  
  // Check server health
  const checkServerHealth = useCallback(async () => {
    try {
      const isOnline = await PokemonTCGService.healthCheck();
      setServerStatus(isOnline);
      return isOnline;
    } catch (error) {
      setServerStatus(false);
      return false;
    }
  }, [setServerStatus]);
  
  // Update card quantity with validation — optimistic update + API persist
  const updateCard = (cardId, variant, quantity) => {
    if (typeof quantity !== 'number' || quantity < 0) {
      console.error('Invalid quantity:', quantity);
      return false;
    }

    // Optimistic: update state immediately
    updateCardQuantity(cardId, variant, quantity);

    // Persist to server in background
    CollectionAPI.updateCard(cardId, variant, quantity).catch(err =>
      console.error('Failed to save card update:', err)
    );
    return true;
  };
  
  // Increment card quantity
  const incrementCard = (cardId, variant) => {
    const currentQty = getVariantQuantity(cardId, variant);
    updateCard(cardId, variant, currentQty + 1);
  };
  
  // Decrement card quantity
  const decrementCard = (cardId, variant) => {
    const currentQty = getVariantQuantity(cardId, variant);
    if (currentQty > 0) {
      updateCard(cardId, variant, currentQty - 1);
    }
  };
  
  // Pack stats operations
  const updatePackCount = (setId, packCount, setInfo = null) => {
    if (typeof packCount !== 'number' || packCount < 0 || packCount > 999) {
      console.error('Invalid pack count:', packCount);
      return false;
    }

    updateSetPackCount(setId, packCount, setInfo);
    return true;
  };
  
  const incrementPackCount = (setId, setInfo = null) => {
    const currentCount = getSetPackCount(setId);
    if (currentCount < 999) {
      updatePackCount(setId, currentCount + 1, setInfo);
    }
  };
  
  const decrementPackCount = (setId, setInfo = null) => {
    const currentCount = getSetPackCount(setId);
    if (currentCount > 0) {
      updatePackCount(setId, currentCount - 1, setInfo);
    }
  };

  // Energy stat operations
  const incrementEnergy = (setId, energyType, isHit = false) => {
    updateEnergyStat(setId, energyType, 1, isHit);
  };

  const decrementEnergy = (setId, energyType) => {
    updateEnergyStat(setId, energyType, -1);
  };

  // Undo a mis-clicked ★ hit without touching the pack count — the pack
  // itself is still owned, only the hit was logged in error.
  const decrementEnergyHit = (setId, energyType) => {
    updateEnergyStat(setId, energyType, 0, false, -1);
  };
  
  // Pokédex binder — fill the card's dex slot (optimistic update + API
  // persist, same split as updateCard). Returns the dex number on success,
  // null if the card can't be slotted.
  const addToPokedex = (card) => {
    const dexNumber = card?.nationalPokedexNumbers?.[0];
    if (!dexNumber || dexNumber > MAX_DEX) return null;

    const slot = {
      cardId: card.id,
      cardName: card.name,
      cardImage: card.images?.small || '',
      setId: card.set?.id || '',
      setName: card.set?.name || '',
      addedAt: new Date().toISOString()
    };

    setPokedexSlot(dexNumber, slot);
    CollectionAPI.savePokedexSlot(dexNumber, slot).catch(err =>
      console.error('Failed to save pokedex slot:', err)
    );
    return dexNumber;
  };

  const removeFromPokedex = (dexNumber) => {
    removePokedexSlotLocal(dexNumber);
    CollectionAPI.removePokedexSlot(dexNumber).catch(err =>
      console.error('Failed to remove pokedex slot:', err)
    );
  };

  // Get series groups for navigation
  const getSeriesGroups = useCallback(() => {
    const groups = {};
    allSets.forEach(set => {
      const series = set.series || 'Other';
      if (!groups[series]) {
        groups[series] = [];
      }
      groups[series].push(set);
    });
    
    // Sort sets within each series by release date (newest first)
    Object.keys(groups).forEach(series => {
      groups[series].sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
    });
    
    return groups;
  }, [allSets]);
  
  // Clear all filters - UPDATED TO INCLUDE ARTIST
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      rarity: 'all',
      type: 'all',
      ownership: 'all',
      artist: 'all'
    });
  }, [setFilters]);
  
  // Toggle completion view
  const toggleCompletionView = useCallback(() => {
    const newView = settings.completionView === 'base' ? 'master' : 'base';
    setSettings({ completionView: newView });
  }, [settings.completionView, setSettings]);
  
  // Export/Import collection via API
  const exportCollection = async () => {
    return CollectionAPI.exportCollection();
  };

  const importCollection = async (data) => {
    try {
      await CollectionAPI.importCollection(data);
      // Reload all data from server after import
      const [collection, packStats, settings] = await Promise.all([
        CollectionAPI.getCollection(),
        CollectionAPI.getPackStats(),
        CollectionAPI.getSettings()
      ]);
      context.setCollection(collection);
      context.setPackStats(packStats);
      setSettings(settings);
      return true;
    } catch (err) {
      console.error('Import failed:', err);
      return false;
    }
  };
  
  return {
    // State
    collection,
    currentSet,
    cards,
    allSets,
    settings,
    filters,
    globalSearch,
    loading,
    serverOnline,
    currentView,
    selectedSeries,
    packStats,
    
    // Data operations
    loadAllSets,
    loadSetCards,
    load151Set,
    performGlobalSearch,
    navigateToCardSet,
    checkServerHealth,
    
    // Collection operations
    updateCardQuantity,
    updateCard,
    incrementCard,
    decrementCard,
    getTotalQuantity,
    getVariantQuantity,
    
    // Pack stats operations
    updatePackCount,
    incrementPackCount,
    decrementPackCount,
    getSetPackCount,

    // Energy stat operations
    incrementEnergy,
    decrementEnergy,
    decrementEnergyHit,
    getEnergyStats,
    getEnergyTypeStats,

    // Pokédex binder
    pokedexBinder,
    addToPokedex,
    removeFromPokedex,
    getPokedexSlot,
    getPokedexCompletion,


    // Calculations
    calculateCompletion,
    getCollectionStats,
    calculateSetCompletion,  // NEW: Added set completion function
    getSetTrackedHitRate,
    getGlobalHitRate,
    getPortfolioValue,
    portfolioLoading,

    // Filters and search
    getFilteredCards,
    getFilterOptions,
    clearFilters,
    
    // Navigation
    setCurrentView,
    setCurrentSet,
    setCards,
    getSeriesGroups,
    
    // Settings
    toggleCompletionView,
    
    // Data management
    exportCollection,
    importCollection,
    
    // Actions
    setFilters,
    setSettings,
    setGlobalSearch
  };
};